import { useState, useCallback } from 'react';
import { supabaseData } from '../../utils/supabase-data-client';
import { refetchAndCache } from './refetchAndCache';
import { useCacheStore } from './useCacheStore';

export type MutationResult = {
  success: boolean;
  error?: string;
  constraintViolation?: boolean;
};

interface DeleteOptions {
  bookmarksReassigned?: boolean;
}

interface UseCategoryMutationsReturn {
  addCategory: (
    name: string,
    parentId: string | null
  ) => Promise<MutationResult>;
  renameCategory: (
    categoryId: string,
    newName: string
  ) => Promise<MutationResult>;
  deleteCategory: (
    categoryId: string,
    options?: DeleteOptions
  ) => Promise<MutationResult>;
  moveCategory: (
    categoryId: string,
    newParentId: string | null
  ) => Promise<MutationResult>;
  loading: boolean;
}

function isUniqueViolation(error: { code?: string }): boolean {
  return error.code === '23505';
}

function validateName(name: string): { valid: boolean; trimmed: string } {
  const trimmed = name.trim();
  if (trimmed.length < 1 || trimmed.length > 100) {
    return { valid: false, trimmed };
  }
  return { valid: true, trimmed };
}

export const useCategoryMutations = (): UseCategoryMutationsReturn => {
  const [loading, setLoading] = useState(false);
  const cacheStore = useCacheStore();

  const addCategory = useCallback(
    async (name: string, parentId: string | null): Promise<MutationResult> => {
      const { valid, trimmed } = validateName(name);
      if (!valid) {
        return {
          success: false,
          error: 'Category name must be between 1 and 100 characters.',
        };
      }

      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('bookmark_categories')
          .insert({ category_name: trimmed, parent_id: parentId });

        if (error) {
          if (isUniqueViolation(error)) {
            return {
              success: false,
              constraintViolation: true,
              error:
                'A category with that name already exists at this level.',
            };
          }
          return { success: false, error: error.message };
        }

        await refetchAndCache('categories', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore]
  );

  const renameCategory = useCallback(
    async (categoryId: string, newName: string): Promise<MutationResult> => {
      const { valid, trimmed } = validateName(newName);
      if (!valid) {
        return {
          success: false,
          error: 'Category name must be between 1 and 100 characters.',
        };
      }

      // Check if name is unchanged by looking up from cache
      const categories = cacheStore.getCategories();
      if (categories) {
        const existing = categories.find(
          (c) => c.category_id === categoryId
        );
        if (existing && existing.category_name === trimmed) {
          return { success: true };
        }
      }

      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('bookmark_categories')
          .update({ category_name: trimmed })
          .eq('category_id', categoryId);

        if (error) {
          if (isUniqueViolation(error)) {
            return {
              success: false,
              constraintViolation: true,
              error:
                'A category with that name already exists at this level.',
            };
          }
          return { success: false, error: error.message };
        }

        await refetchAndCache('categories', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore]
  );

  const deleteCategory = useCallback(
    async (
      categoryId: string,
      options?: DeleteOptions
    ): Promise<MutationResult> => {
      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('bookmark_categories')
          .delete()
          .eq('category_id', categoryId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('categories', cacheStore);

        if (options?.bookmarksReassigned) {
          await refetchAndCache('bookmarks', cacheStore);
        }

        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore]
  );

  const moveCategory = useCallback(
    async (
      categoryId: string,
      newParentId: string | null
    ): Promise<MutationResult> => {
      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('bookmark_categories')
          .update({ parent_id: newParentId })
          .eq('category_id', categoryId);

        if (error) {
          if (isUniqueViolation(error)) {
            return {
              success: false,
              constraintViolation: true,
              error:
                'A category with that name already exists at the destination level.',
            };
          }
          return { success: false, error: error.message };
        }

        await refetchAndCache('categories', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore]
  );

  return {
    addCategory,
    renameCategory,
    deleteCategory,
    moveCategory,
    loading,
  };
};
